/**
 * SuperAdmin Dashboard
 * Comprehensive admin interface for managing all site content and users
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import BlogEditModal from '../components/BlogEditModal';
import EditableModal from '../components/EditableModal';
import RichTextEditor from '../components/RichTextEditor';
import AnalyticsTab from '../components/AnalyticsTab';
import { AVAILABLE_COURSES, ROLE_OPTIONS, LANDING_PAGE_OPTIONS } from '../config/adminConfig';
import {
  FiUsers,
  FiFileText,
  FiMessageSquare,
  FiMail,
  FiSettings,
  FiGrid,
  FiBarChart2,
  FiTrendingUp,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiX,
  FiCheckCircle,
  FiXCircle,
  FiEye,
  FiRefreshCw,
  FiCheck,
  FiRss,
  FiLink
} from 'react-icons/fi';

const SuperAdmin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [refreshKey, setRefreshKey] = useState(0);

  // Redirect non-admins
  useEffect(() => {
    if (user && user.role !== 'admin') {
      window.location.href = '/';
    }
  }, [user]);

  const tabs = [
    { id: 'users', label: 'Users', icon: FiUsers },
    { id: 'blogs', label: 'Blog Posts', icon: FiFileText },
    { id: 'comments', label: 'Comments', icon: FiMessageSquare },
    { id: 'contacts', label: 'Contact Messages', icon: FiMail },
    { id: 'content', label: 'Content Items', icon: FiGrid },
    { id: 'linktree', label: 'Link Tree', icon: FiLink },
    { id: 'configs', label: 'Configuration', icon: FiSettings },
    { id: 'stats', label: 'Statistics', icon: FiBarChart2 },
    { id: 'analytics', label: 'Analytics', icon: FiTrendingUp }
  ];

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    toast.info('Refreshing data...');
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <DashboardContainer>
      <Header>
        <Title>Super Admin Dashboard</Title>
        <RefreshButton onClick={handleRefresh}>
          <FiRefreshCw /> Refresh
        </RefreshButton>
      </Header>

      <TabNav>
        {tabs.map(tab => (
          <Tab
            key={tab.id}
            $active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon />
            <span>{tab.label}</span>
          </Tab>
        ))}
      </TabNav>

      <ContentArea>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'users' && <UsersPanel refreshKey={refreshKey} />}
            {activeTab === 'blogs' && <BlogsPanel refreshKey={refreshKey} />}
            {activeTab === 'comments' && <CommentsPanel refreshKey={refreshKey} />}
            {activeTab === 'contacts' && <ContactsPanel refreshKey={refreshKey} />}
            {activeTab === 'content' && <ContentPanel refreshKey={refreshKey} />}
            {activeTab === 'linktree' && <LinkTreePanel refreshKey={refreshKey} />}
            {activeTab === 'configs' && <ConfigsPanel refreshKey={refreshKey} />}
            {activeTab === 'stats' && <StatsPanel refreshKey={refreshKey} />}
            {activeTab === 'analytics' && <AnalyticsTab refreshKey={refreshKey} />}
          </motion.div>
        </AnimatePresence>
      </ContentArea>
    </DashboardContainer>
  );
};

// ========================================
// USERS PANEL - EXACT COPY FROM AdminDashboard
// ========================================
const UsersPanel = ({ refreshKey }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalUser, setEditModalUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: null, name: '' });

  // Get config values
  const [availableCourses, setAvailableCourses] = useState([]);
  const roleOptions = ROLE_OPTIONS;
  const landingPageOptions = LANDING_PAGE_OPTIONS;

  // Fetch available courses from ContentItems API
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await api.get('/content-items/courses/dropdown');
        setAvailableCourses(response.data.data || []);
      } catch (error) {
        console.error('Failed to load courses:', error);
        // Fallback to hardcoded constant if API fails
        setAvailableCourses(AVAILABLE_COURSES);
        toast.error('Failed to load courses, using fallback values');
      }
    };

    fetchCourses();
  }, []);

  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    landingPage: '/',
    courseAccess: [],
    isActive: true
  });

  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    landingPage: '/',
    courseAccess: [],
    isActive: true
  });

  useEffect(() => {
    fetchUsers();
  }, [refreshKey]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/users');
      setUsers(res.data.users);
    } catch (error) {
      toast.error('Failed to load users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (userId) => {
    try {
      const userToUpdate = users.find(u => u._id === userId);
      const response = await api.put(`/admin/users/${userId}`, {
        isActive: !userToUpdate.isActive
      });
      setUsers(users.map(u => u._id === userId ? response.data.user : u));
      toast.success(`User ${response.data.user.isActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      const response = await api.put(`/admin/users/${userId}/approve`);
      setUsers(users.map(u => u._id === userId ? response.data.user : u));
      toast.success('User approved successfully');
    } catch (error) {
      console.error('Failed to approve user:', error);
      toast.error(error.response?.data?.message || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user? This action cannot be undone.')) {
      return;
    }
    try {
      const response = await api.put(`/admin/users/${userId}/reject`, {
        reason: 'Rejected by administrator'
      });
      setUsers(users.map(u => u._id === userId ? response.data.user : u));
      toast.success('User rejected');
    } catch (error) {
      console.error('Failed to reject user:', error);
      toast.error(error.response?.data?.message || 'Failed to reject user');
    }
  };

  const handleChangeStatus = async (userId, newStatus) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, {
        status: newStatus
      });
      setUsers(users.map(u => u._id === userId ? response.data.user : u));
      toast.success(`User status changed to ${newStatus}`);
    } catch (error) {
      console.error('Failed to change user status:', error);
      toast.error(error.response?.data?.message || 'Failed to change user status');
    }
  };

  // Delete user (from AdminDashboard)
  const handleDeleteUser = async (userId, userName) => {
    setDeleteTarget({ id: userId, name: userName });
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      setUsers(users.filter(u => u._id !== deleteTarget.id));
      setShowDeleteModal(false);
      setDeleteTarget({ id: null, name: '' });
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  // Create user handlers (from AdminDashboard)
  const handleOpenCreateModal = () => {
    setCreateForm({
      name: '',
      email: '',
      password: '',
      role: 'user',
      landingPage: '/',
      courseAccess: [],
      isActive: true
    });
    setShowCreateModal(true);
  };

  const handleCreateFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleCourseInCreate = (courseId) => {
    setCreateForm(prev => ({
      ...prev,
      courseAccess: prev.courseAccess.includes(courseId)
        ? prev.courseAccess.filter(id => id !== courseId)
        : [...prev.courseAccess, courseId]
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Name, email, and password are required');
      return;
    }

    try {
      const response = await api.post('/admin/users', createForm);
      setUsers([...users, response.data.user]);
      setShowCreateModal(false);
      toast.success('User created successfully');
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  // Edit user handlers (from AdminDashboard)
  const handleOpenEditModal = (u) => {
    setEditModalUser(u);
    setEditForm({
      name: u.name,
      email: u.email,
      role: u.role,
      landingPage: u.landingPage || '/',
      courseAccess: u.courseAccess || [],
      isActive: u.isActive,
      password: ''
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleCourseInEdit = (courseId) => {
    setEditForm(prev => ({
      ...prev,
      courseAccess: prev.courseAccess.includes(courseId)
        ? prev.courseAccess.filter(id => id !== courseId)
        : [...prev.courseAccess, courseId]
    }));
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    if (!editForm.name || !editForm.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const updateData = { ...editForm };
      if (!updateData.password) {
        delete updateData.password;
      }

      const response = await api.put(`/admin/users/${editModalUser._id}`, updateData);
      setUsers(users.map(u => u._id === editModalUser._id ? response.data.user : u));
      setShowEditModal(false);
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  if (loading) return <LoadingState>Loading users...</LoadingState>;

  return (
    <PanelContainer>
      <PanelHeader>
        <h2>User Management</h2>
        <ActionButton onClick={handleOpenCreateModal}>
          <FiPlus /> Create User
        </ActionButton>
      </PanelHeader>

      {/* Users Table - EXACT from AdminDashboard */}
      <UsersTable>
        <TableHeader>
          <HeaderCell>User</HeaderCell>
          <HeaderCell>Email</HeaderCell>
          <HeaderCell>Role</HeaderCell>
          <HeaderCell>Landing Page</HeaderCell>
          <HeaderCell>Course Access</HeaderCell>
          <HeaderCell>Status</HeaderCell>
          <HeaderCell>Actions</HeaderCell>
        </TableHeader>

        <TableBody>
          {users.map((u, index) => (
            <TableRow
              key={u._id}
              as={motion.div}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Cell>
                <UserInfo>
                  <UserStatusIndicator 
                    isLoggedIn={u.lastLogin && (Date.now() - new Date(u.lastLogin).getTime()) < 3600000} // Logged in within last hour
                    hasIssue={u.status === 'pending' || u.status === 'rejected'}
                  />
                  <UserAvatar>{u.name.charAt(0).toUpperCase()}</UserAvatar>
                  <UserName>{u.name}</UserName>
                </UserInfo>
              </Cell>
              <Cell>
                <UserEmail>{u.email}</UserEmail>
              </Cell>
              <Cell>
                <RoleBadge role={u.role}>{u.role}</RoleBadge>
              </Cell>
              <Cell>
                <LandingPageBadge>{u.landingPage || '/'}</LandingPageBadge>
              </Cell>
              <Cell>
                <CourseList>
                  {u.courseAccess && u.courseAccess.length > 0 ? (
                    u.courseAccess.map(course => (
                      <CourseBadge key={course}>{course}</CourseBadge>
                    ))
                  ) : (
                    <NoAccess>No course access</NoAccess>
                  )}
                </CourseList>
              </Cell>
              <Cell>
                <StatusContainer>
                  <StatusBadge active={u.isActive}>
                    {u.isActive ? (
                      <><FiCheckCircle /> Active</>
                    ) : (
                      <><FiXCircle /> Inactive</>
                    )}
                  </StatusBadge>
                  {u._id !== currentUser?.id && (
                    <StatusSelect
                      value={u.status || 'pending'}
                      onChange={(e) => handleChangeStatus(u._id, e.target.value)}
                      title="Change user status"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </StatusSelect>
                  )}
                </StatusContainer>
              </Cell>
              <Cell>
                <AdminActionButtons>
                  <AdminActionButton
                    onClick={() => handleOpenEditModal(u)}
                    title="Edit user"
                  >
                    <FiEdit2 />
                  </AdminActionButton>
                  {u.status === 'pending' && u._id !== currentUser?.id && (
                    <>
                      <AdminActionButton
                        onClick={() => handleApproveUser(u._id)}
                        title="Approve user"
                        style={{ color: '#10b981' }}
                      >
                        <FiCheck />
                      </AdminActionButton>
                      <AdminActionButton
                        onClick={() => handleRejectUser(u._id)}
                        title="Reject user"
                        style={{ color: '#ef4444' }}
                      >
                        <FiX />
                      </AdminActionButton>
                    </>
                  )}
                  <AdminActionButton
                    onClick={() => handleToggleActive(u._id)}
                    disabled={u._id === currentUser?.id}
                    title={u.isActive ? 'Deactivate user' : 'Activate user'}
                  >
                    {u.isActive ? <FiXCircle /> : <FiCheckCircle />}
                  </AdminActionButton>
                  <AdminActionButton
                    danger
                    onClick={() => handleDeleteUser(u._id, u.name)}
                    disabled={u._id === currentUser?.id}
                    title="Delete user"
                  >
                    <FiTrash2 />
                  </AdminActionButton>
                </AdminActionButtons>
              </Cell>
            </TableRow>
          ))}
        </TableBody>
      </UsersTable>

      {/* Create User Modal - EXACT from AdminDashboard */}
      <AnimatePresence>
        {showCreateModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCreateModal(false)}
          >
            <ModalContent
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>Create New User</ModalTitle>
                <CloseButton onClick={() => setShowCreateModal(false)}>
                  <FiX />
                </CloseButton>
              </ModalHeader>

              <ModalForm onSubmit={handleCreateUser}>
                <FormRow>
                  <FormGroup>
                    <Label>Name *</Label>
                    <Input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => handleCreateFormChange('name', e.target.value)}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={createForm.email}
                      onChange={(e) => handleCreateFormChange('email', e.target.value)}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      value={createForm.password}
                      onChange={(e) => handleCreateFormChange('password', e.target.value)}
                      required
                      minLength="6"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Role</Label>
                    <Select
                      value={createForm.role}
                      onChange={(e) => handleCreateFormChange('role', e.target.value)}
                    >
                      {roleOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Landing Page</Label>
                    <Select
                      value={createForm.landingPage}
                      onChange={(e) => handleCreateFormChange('landingPage', e.target.value)}
                    >
                      {landingPageOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Course Access</Label>
                  <CheckboxGroup>
                    {availableCourses.map(course => (
                      <CheckboxLabel key={course.id}>
                        <input
                          type="checkbox"
                          checked={createForm.courseAccess.includes(course.id)}
                          onChange={() => handleToggleCourseInCreate(course.id)}
                        />
                        <span>{course.name}</span>
                      </CheckboxLabel>
                    ))}
                  </CheckboxGroup>
                </FormGroup>

                <FormGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={createForm.isActive}
                      onChange={(e) => handleCreateFormChange('isActive', e.target.checked)}
                    />
                    <span>Account Active</span>
                  </CheckboxLabel>
                </FormGroup>

                <ModalActions>
                  <CancelButton type="button" onClick={() => setShowCreateModal(false)}>
                    Cancel
                  </CancelButton>
                  <SubmitButton type="submit">Create User</SubmitButton>
                </ModalActions>
              </ModalForm>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Edit User Modal - EXACT from AdminDashboard */}
      <AnimatePresence>
        {showEditModal && editModalUser && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowEditModal(false)}
          >
            <ModalContent
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>Edit User: {editModalUser.name}</ModalTitle>
                <CloseButton onClick={() => setShowEditModal(false)}>
                  <FiX />
                </CloseButton>
              </ModalHeader>

              <ModalForm onSubmit={handleUpdateUser}>
                <FormRow>
                  <FormGroup>
                    <Label>Name *</Label>
                    <Input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => handleEditFormChange('name', e.target.value)}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => handleEditFormChange('email', e.target.value)}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>New Password (leave blank to keep current)</Label>
                    <Input
                      type="password"
                      value={editForm.password}
                      onChange={(e) => handleEditFormChange('password', e.target.value)}
                      minLength="6"
                      placeholder="Leave blank to keep current password"
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Role</Label>
                    <Select
                      value={editForm.role}
                      onChange={(e) => handleEditFormChange('role', e.target.value)}
                      disabled={editModalUser._id === currentUser?.id}
                    >
                      {roleOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Landing Page</Label>
                    <Select
                      value={editForm.landingPage}
                      onChange={(e) => handleEditFormChange('landingPage', e.target.value)}
                    >
                      {landingPageOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Course Access</Label>
                  <CheckboxGroup>
                    {availableCourses.map(course => (
                      <CheckboxLabel key={course.id}>
                        <input
                          type="checkbox"
                          checked={editForm.courseAccess.includes(course.id)}
                          onChange={() => handleToggleCourseInEdit(course.id)}
                        />
                        <span>{course.name}</span>
                      </CheckboxLabel>
                    ))}
                  </CheckboxGroup>
                </FormGroup>

                <FormGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={(e) => handleEditFormChange('isActive', e.target.checked)}
                      disabled={editModalUser._id === currentUser?.id}
                    />
                    <span>Account Active</span>
                  </CheckboxLabel>
                </FormGroup>

                <ModalActions>
                  <CancelButton type="button" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </CancelButton>
                  <SubmitButton type="submit">Update User</SubmitButton>
                </ModalActions>
              </ModalForm>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteModal(false)}
          >
            <ModalContent
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '500px' }}
            >
              <ModalHeader>
                <ModalTitle>Confirm Delete</ModalTitle>
                <CloseButton onClick={() => setShowDeleteModal(false)}>
                  <FiX />
                </CloseButton>
              </ModalHeader>

              <DeleteModalBody>
                <DeleteWarningIcon>
                  <FiTrash2 />
                </DeleteWarningIcon>
                <DeleteMessage>
                  Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
                </DeleteMessage>
                <DeleteWarning>
                  This action cannot be undone. All user data will be permanently removed.
                </DeleteWarning>
              </DeleteModalBody>

              <ModalActions>
                <CancelButton type="button" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </CancelButton>
                <DeleteButton type="button" onClick={confirmDeleteUser}>
                  Delete User
                </DeleteButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </PanelContainer>
  );
};

// ========================================
// BLOGS PANEL
// ========================================
const BlogsPanel = ({ refreshKey }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({});
  const [selectedBlogs, setSelectedBlogs] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [rssStatus, setRssStatus] = useState(null);
  const [validatingRSS, setValidatingRSS] = useState(false);

  useEffect(() => {
    fetchBlogs();
    validateRSSFeed();
  }, [refreshKey]);

  const validateRSSFeed = async () => {
    setValidatingRSS(true);
    try {
      const res = await api.get('/feed/validate');
      if (res.data.success) {
        setRssStatus(res.data.data);
      }
    } catch (error) {
      console.error('Failed to validate RSS feed:', error);
    } finally {
      setValidatingRSS(false);
    }
  };

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/content-items?contentType=blog');
      setBlogs(res.data.data);
    } catch (error) {
      toast.error('Failed to load blogs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      await api.delete(`/content-items/${blogId}`);
      toast.success('Blog post deleted');
      fetchBlogs();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const handleTogglePublish = async (blog) => {
    try {
      await api.put(`/content-items/${blog._id}`, {
        ...blog,
        metadata: {
          ...blog.metadata,
          published: !blog.metadata?.published
        }
      });
      toast.success(`Blog ${blog.metadata?.published ? 'unpublished' : 'published'}. RSS feed updated.`);
      fetchBlogs();
      validateRSSFeed(); // Refresh RSS status
    } catch (error) {
      toast.error('Failed to update blog');
    }
  };

  const handleSelectBlog = (blogId) => {
    setSelectedBlogs(prev => 
      prev.includes(blogId) 
        ? prev.filter(id => id !== blogId)
        : [...prev, blogId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBlogs.length === blogs.length) {
      setSelectedBlogs([]);
    } else {
      setSelectedBlogs(blogs.map(blog => blog._id));
    }
  };

  const handleBulkPublish = async (published) => {
    if (selectedBlogs.length === 0) {
      toast.warning('Please select at least one blog post');
      return;
    }

    setBulkActionLoading(true);
    try {
      await api.patch('/content-items/bulk-publish', {
        ids: selectedBlogs,
        published
      });
      toast.success(`${selectedBlogs.length} blog post(s) ${published ? 'published' : 'unpublished'}. RSS feed updated.`);
      setSelectedBlogs([]);
      fetchBlogs();
      validateRSSFeed(); // Refresh RSS status
    } catch (error) {
      toast.error(`Failed to ${published ? 'publish' : 'unpublish'} blog posts`);
      console.error(error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      contentType: 'blog',
      title: '',
      category: '',
      data: {
        excerpt: '',
        content: '',
        tags: [],
        author: '',
        date: new Date().toISOString().split('T')[0]
      },
      metadata: {
        published: false
      }
    });
    setShowModal(true);
  };

  const handleEdit = (blog) => {
    setIsCreating(false);
    setFormData(blog);
    setShowModal(true);
  };

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await api.post('/content-items', formData);
        toast.success('Blog post created');
      } else {
        await api.put(`/content-items/${formData._id}`, formData);
        toast.success('Blog post updated');
      }
      setShowModal(false);
      fetchBlogs();
    } catch (error) {
      toast.error(`Failed to ${isCreating ? 'create' : 'update'} blog post`);
      console.error(error);
    }
  };

  if (loading) return <LoadingState>Loading blog posts...</LoadingState>;

  return (
    <PanelContainer>
      <PanelHeader>
        <h2>Blog Posts Management</h2>
        <HeaderActions>
          {selectedBlogs.length > 0 && (
            <BulkActions>
              <BulkButton onClick={() => handleBulkPublish(true)} disabled={bulkActionLoading}>
                <FiCheckCircle /> Publish Selected ({selectedBlogs.length})
              </BulkButton>
              <BulkButton onClick={() => handleBulkPublish(false)} disabled={bulkActionLoading} $secondary>
                <FiXCircle /> Unpublish Selected ({selectedBlogs.length})
              </BulkButton>
            </BulkActions>
          )}
          <ActionButton onClick={handleCreate}>
            <FiPlus /> Create Blog Post
          </ActionButton>
        </HeaderActions>
      </PanelHeader>

      <Table>
        <thead>
          <tr>
            <th style={{ width: '40px' }}>
              <Checkbox
                type="checkbox"
                checked={selectedBlogs.length === blogs.length && blogs.length > 0}
                onChange={handleSelectAll}
              />
            </th>
            <th>Title</th>
            <th>Category</th>
            <th>Author</th>
            <th>Date</th>
            <th>Status</th>
            <th>Views</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {blogs.map(blog => (
            <tr key={blog._id}>
              <td>
                <Checkbox
                  type="checkbox"
                  checked={selectedBlogs.includes(blog._id)}
                  onChange={() => handleSelectBlog(blog._id)}
                />
              </td>
              <td>
                <strong>{blog.title}</strong>
                <br />
                <small style={{ color: '#666' }}>{blog.data?.excerpt?.substring(0, 80)}...</small>
              </td>
              <td>{blog.category}</td>
              <td>{blog.data?.author || 'N/A'}</td>
              <td>{blog.data?.date ? new Date(blog.data.date).toLocaleDateString() : 'N/A'}</td>
              <td>
                {blog.metadata?.published ? (
                  <Badge $variant="success">Published</Badge>
                ) : (
                  <Badge $variant="warning">Draft</Badge>
                )}
              </td>
              <td>{blog.data?.views || 0}</td>
              <td>
                <ActionButtons>
                  <IconButton onClick={() => window.open(`/blog/${blog._id}`, '_blank')} title="View">
                    <FiEye />
                  </IconButton>
                  <IconButton onClick={() => handleEdit(blog)} title="Edit">
                    <FiEdit2 />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleTogglePublish(blog)} 
                    title={blog.metadata?.published ? 'Unpublish' : 'Publish'}
                  >
                    {blog.metadata?.published ? <FiXCircle /> : <FiCheckCircle />}
                  </IconButton>
                  <IconButton onClick={() => handleDelete(blog._id)} title="Delete" $danger>
                    <FiTrash2 />
                  </IconButton>
                </ActionButtons>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {showModal && (
        <BlogEditModal
          isOpen={showModal}
          isCreating={isCreating}
          onClose={() => {
            setShowModal(false);
            validateRSSFeed(); // Refresh RSS status after modal closes
          }}
          onSave={async () => {
            await handleSave();
            validateRSSFeed(); // Refresh RSS status after save
          }}
          formData={formData}
          updateFormData={updateFormData}
        />
      )}

      {/* RSS Feed Status */}
      <RSSStatusCard>
        <RSSStatusHeader>
          <h3>RSS Feed Status</h3>
          <RefreshButton onClick={validateRSSFeed} disabled={validatingRSS}>
            <FiRefreshCw /> {validatingRSS ? 'Validating...' : 'Refresh'}
          </RefreshButton>
        </RSSStatusHeader>
        {rssStatus && (
          <RSSStatusContent>
            <StatusRow>
              <StatusLabel>Status:</StatusLabel>
              <RSSStatusBadge $valid={rssStatus.valid}>
                {rssStatus.valid ? 'Valid' : 'Issues Found'}
              </RSSStatusBadge>
            </StatusRow>
            <StatusRow>
              <StatusLabel>Feed URL:</StatusLabel>
              <RSSLink href={rssStatus.feedUrl} target="_blank" rel="noopener noreferrer">
                {rssStatus.feedUrl}
              </RSSLink>
            </StatusRow>
            <StatusRow>
              <StatusLabel>Published Posts:</StatusLabel>
              <StatusValue>{rssStatus.postCount}</StatusValue>
            </StatusRow>
            {rssStatus.issues.length > 0 && (
              <IssuesList>
                <strong>Issues:</strong>
                {rssStatus.issues.map((issue, idx) => (
                  <IssueItem key={idx}>⚠️ {issue}</IssueItem>
                ))}
              </IssuesList>
            )}
            {rssStatus.warnings.length > 0 && (
              <WarningsList>
                <strong>Warnings:</strong>
                {rssStatus.warnings.map((warning, idx) => (
                  <WarningItem key={idx}>ℹ️ {warning}</WarningItem>
                ))}
              </WarningsList>
            )}
            <FeedLinks>
              <FeedLink href={`${process.env.REACT_APP_API_BASE || 'http://localhost:5001'}/api/feed/rss`} target="_blank" rel="noopener noreferrer">
                <FiRss /> RSS Feed
              </FeedLink>
              <FeedLink href={`${process.env.REACT_APP_API_BASE || 'http://localhost:5001'}/api/feed/atom`} target="_blank" rel="noopener noreferrer">
                <FiRss /> Atom Feed
              </FeedLink>
            </FeedLinks>
          </RSSStatusContent>
        )}
      </RSSStatusCard>
    </PanelContainer>
  );
};

// ========================================
// COMMENTS PANEL
// ========================================
const CommentsPanel = ({ refreshKey }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComments();
  }, [refreshKey]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/comments/admin/all');
      setComments(res.data.data);
    } catch (error) {
      toast.error('Failed to load comments');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await api.delete(`/comments/${commentId}`);
      toast.success('Comment deleted');
      fetchComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleToggleApprove = async (comment) => {
    try {
      await api.patch(`/comments/${comment._id}/approve`, {
        isApproved: !comment.isApproved
      });
      toast.success(`Comment ${comment.isApproved ? 'unapproved' : 'approved'}`);
      fetchComments();
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  if (loading) return <LoadingState>Loading comments...</LoadingState>;

  return (
    <PanelContainer>
      <PanelHeader>
        <h2>Comments Moderation</h2>
        <span>{comments.length} total comments</span>
      </PanelHeader>

      <Table>
        <thead>
          <tr>
            <th>User</th>
            <th>Comment</th>
            <th>Blog Post</th>
            <th>Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {comments.map(comment => (
            <tr key={comment._id}>
              <td>
                <strong>{comment.user?.name || 'Unknown'}</strong>
                <br />
                <small style={{ color: '#666' }}>{comment.user?.email}</small>
              </td>
              <td>
                <CommentText>{comment.text}</CommentText>
                {comment.isEdited && (
                  <small style={{ color: '#999', display: 'block', marginTop: '4px' }}>
                    Edited {new Date(comment.editedAt).toLocaleString()}
                  </small>
                )}
              </td>
              <td>
                <small>{comment.contentItem?.title || 'Deleted post'}</small>
              </td>
              <td>{new Date(comment.createdAt).toLocaleDateString()}</td>
              <td>
                {comment.isApproved ? (
                  <Badge $variant="success">Approved</Badge>
                ) : (
                  <Badge $variant="warning">Pending</Badge>
                )}
              </td>
              <td>
                <ActionButtons>
                  <IconButton 
                    onClick={() => handleToggleApprove(comment)} 
                    title={comment.isApproved ? 'Unapprove' : 'Approve'}
                  >
                    {comment.isApproved ? <FiXCircle /> : <FiCheckCircle />}
                  </IconButton>
                  <IconButton onClick={() => handleDelete(comment._id)} title="Delete" $danger>
                    <FiTrash2 />
                  </IconButton>
                </ActionButtons>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </PanelContainer>
  );
};

// ========================================
// CONTACTS PANEL
// ========================================
const ContactsPanel = ({ refreshKey }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, [refreshKey]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/contact/messages');
      setContacts(res.data.messages || res.data.data || []);
    } catch (error) {
      toast.error('Failed to load contact messages');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await api.delete(`/contact/messages/${contactId}`);
      toast.success('Message deleted');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  if (loading) return <LoadingState>Loading contact messages...</LoadingState>;

  return (
    <PanelContainer>
      <PanelHeader>
        <h2>Contact Messages</h2>
        <span>{contacts.length} messages</span>
      </PanelHeader>

      <Table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Subject</th>
            <th>Message</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {contacts.map(contact => (
            <tr key={contact._id}>
              <td><strong>{contact.name}</strong></td>
              <td>{contact.email}</td>
              <td>{contact.subject}</td>
              <td>
                <CommentText>{contact.message}</CommentText>
              </td>
              <td>{new Date(contact.createdAt).toLocaleDateString()}</td>
              <td>
                <ActionButtons>
                  <IconButton 
                    as="a" 
                    href={`mailto:${contact.email}?subject=Re: ${contact.subject}`}
                    title="Reply"
                  >
                    <FiMail />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(contact._id)} title="Delete" $danger>
                    <FiTrash2 />
                  </IconButton>
                </ActionButtons>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </PanelContainer>
  );
};

// ========================================
// CONTENT PANEL
// ========================================
const ContentPanel = ({ refreshKey }) => {
  const [contentItems, setContentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const contentTypes = [
    'all', 'hero', 'paragraph', 'skill', 'experience', 'job', 
    'portfolio', 'teaching', 'contact-info', 'page-section'
  ];

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, filterType]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const url = filterType === 'all' 
        ? '/content-items' 
        : `/content-items?contentType=${filterType}`;
      const res = await api.get(url);
      setContentItems(res.data.data);
    } catch (error) {
      toast.error('Failed to load content items');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await api.delete(`/content-items/${itemId}`);
      toast.success('Item deleted');
      fetchContent();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || '',
      subtitle: item.subtitle || '',
      shortDescription: item.shortDescription || '',
      description: item.description || '',
      category: item.category || '',
      order: item.order || item.metadata?.order || 0,
      published: item.metadata?.published || false,
      page: item.page || item.metadata?.page || '',
      section: item.section || item.metadata?.section || '',
      data: item.data || {}
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    try {
      await api.put(`/content-items/${editingItem._id}`, formData);
      toast.success('Item updated successfully');
      setShowEditModal(false);
      setEditingItem(null);
      fetchContent();
    } catch (error) {
      toast.error('Failed to update item');
      console.error(error);
    }
  };

  const handleCancel = () => {
    setShowEditModal(false);
    setEditingItem(null);
    setFormData({});
  };

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  if (loading) return <LoadingState>Loading content...</LoadingState>;

  return (
    <PanelContainer>
      <PanelHeader>
        <h2>Content Items Management</h2>
        <FilterSelect value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          {contentTypes.map(type => (
            <option key={type} value={type}>
              {type === 'all' ? 'All Types' : type}
            </option>
          ))}
        </FilterSelect>
      </PanelHeader>

      <Table>
        <thead>
          <tr>
            <th>Type</th>
            <th>Title</th>
            <th>Category</th>
            <th>Page</th>
            <th>Section</th>
            <th>Published</th>
            <th>Order</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {contentItems.map(item => (
            <tr key={item._id}>
              <td>
                <Badge $variant="info">{item.contentType}</Badge>
              </td>
              <td>
                <strong>{item.title || item.subtitle || 'No title'}</strong>
                <br />
                <small style={{ color: '#666' }}>
                  {item.shortDescription?.substring(0, 50)}
                </small>
              </td>
              <td>{item.category || '-'}</td>
              <td>{item.metadata?.page || item.page || '-'}</td>
              <td>{item.metadata?.section || item.section || '-'}</td>
              <td>
                {item.metadata?.published ? (
                  <Badge $variant="success">Yes</Badge>
                ) : (
                  <Badge $variant="warning">No</Badge>
                )}
              </td>
              <td>{item.metadata?.order || item.order || 0}</td>
              <td>
                <ActionButtons>
                  <IconButton 
                    onClick={() => window.location.href = `/${item.metadata?.page || item.page || ''}`}
                    title="View Page"
                  >
                    <FiEye />
                  </IconButton>
                  <IconButton onClick={() => handleEdit(item)} title="Edit">
                    <FiEdit2 />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(item._id)} title="Delete" $danger>
                    <FiTrash2 />
                  </IconButton>
                </ActionButtons>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Edit Content Item Modal */}
      <EditableModal
        isOpen={showEditModal}
        isCreating={false}
        onClose={handleCancel}
        onSave={handleSave}
        title={`Edit ${editingItem?.contentType || 'Content Item'}`}
      >
        <label>Title</label>
        <input
          type="text"
          value={formData.title || ''}
          onChange={e => updateFormData({ title: e.target.value })}
          placeholder="Title"
        />

        <label>Subtitle</label>
        <input
          type="text"
          value={formData.subtitle || ''}
          onChange={e => updateFormData({ subtitle: e.target.value })}
          placeholder="Subtitle"
        />

        <label>Short Description</label>
        <textarea
          value={formData.shortDescription || ''}
          onChange={e => updateFormData({ shortDescription: e.target.value })}
          placeholder="Short description"
          rows={3}
        />

        <label>Description</label>
        <RichTextEditor
          value={formData.description || ''}
          onChange={(content) => updateFormData({ description: content })}
          placeholder="Full description"
          height="200px"
        />

        <label>Category</label>
        <input
          type="text"
          value={formData.category || ''}
          onChange={e => updateFormData({ category: e.target.value })}
          placeholder="Category"
        />

        <label>Page</label>
        <input
          type="text"
          value={formData.page || ''}
          onChange={e => updateFormData({ page: e.target.value })}
          placeholder="Page (e.g., about, jobs)"
        />

        <label>Section</label>
        <input
          type="text"
          value={formData.section || ''}
          onChange={e => updateFormData({ section: e.target.value })}
          placeholder="Section"
        />

        <label>Display Order</label>
        <input
          type="number"
          value={formData.order || 0}
          onChange={e => updateFormData({ order: parseInt(e.target.value) })}
          placeholder="Display order"
        />

        <label>
          <input
            type="checkbox"
            checked={formData.published || false}
            onChange={e => updateFormData({ published: e.target.checked })}
          />
          {' '}Published
        </label>
      </EditableModal>
    </PanelContainer>
  );
};

// ========================================
// LINKTREE PANEL
// ========================================
const LinkTreePanel = ({ refreshKey }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    url: '',
    icon: '',
    order: 0,
    published: true
  });
  // Contact card state
  const [showContactCard, setShowContactCard] = useState(false);
  const [contactCard, setContactCard] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    organization: '',
    website: '',
    linkedin: '',
    twitter: '',
    github: '',
    address: '',
    note: ''
  });
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    fetchLinks();
    fetchContactCard();
  }, [refreshKey]);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const res = await api.get('/linktree/all');
      setLinks(res.data.links || []);
    } catch (error) {
      toast.error('Failed to load links');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContactCard = async () => {
    try {
      const res = await api.get('/linktree/contact-card');
      if (res.data.config) {
        setContactCard(res.data.config);
      }
    } catch (error) {
      console.error('Failed to load contact card:', error);
    }
  };

  const handleSaveContactCard = async () => {
    setSavingContact(true);
    try {
      await api.put('/linktree/contact-card', contactCard);
      toast.success('Contact card saved successfully');
    } catch (error) {
      toast.error('Failed to save contact card');
      console.error(error);
    } finally {
      setSavingContact(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      title: '',
      subtitle: '',
      url: '',
      icon: '',
      order: links.length,
      published: true
    });
    setEditingLink(null);
    setShowCreateModal(true);
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title || '',
      subtitle: link.subtitle || '',
      url: link.url || '',
      icon: link.icon || '',
      order: link.order || 0,
      published: link.published !== false
    });
    setShowCreateModal(false);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingLink) {
        await api.put(`/linktree/${editingLink._id}`, formData);
        toast.success('Link updated successfully');
        setShowEditModal(false);
      } else {
        await api.post('/linktree', formData);
        toast.success('Link created successfully');
        setShowCreateModal(false);
      }
      setEditingLink(null);
      setFormData({
        title: '',
        subtitle: '',
        url: '',
        icon: '',
        order: 0,
        published: true
      });
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save link');
      console.error(error);
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    
    try {
      await api.delete(`/linktree/${linkId}`);
      toast.success('Link deleted');
      fetchLinks();
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingLink(null);
    setFormData({
      title: '',
      subtitle: '',
      url: '',
      icon: '',
      order: 0,
      published: true
    });
  };

  if (loading) return <LoadingState>Loading links...</LoadingState>;

  return (
    <PanelContainer>
      <PanelHeader>
        <h2>Link Tree Management</h2>
        <ActionButton onClick={handleCreate}>
          <FiPlus /> Add Link
        </ActionButton>
      </PanelHeader>

      <InfoBox>
        <strong>Short URL:</strong> <code><a href={`${window.location.origin}/l`} target="_blank" rel="noopener noreferrer">{window.location.origin}/l</a></code>
        <br />
        <strong>Full URL:</strong> <code>{window.location.origin}/r/links</code>
      </InfoBox>

      {/* Contact Card Configuration */}
      <CollapsibleSection>
        <CollapsibleHeader onClick={() => setShowContactCard(!showContactCard)}>
          <span>Contact Card (vCard) Configuration</span>
          <span style={{ transform: showContactCard ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>▼</span>
        </CollapsibleHeader>
        {showContactCard && (
          <CollapsibleContent>
            <FormGrid>
              <ContactFormGroup>
                <label>First Name</label>
                <input
                  type="text"
                  value={contactCard.firstName}
                  onChange={e => setContactCard({ ...contactCard, firstName: e.target.value })}
                  placeholder="Richard"
                />
              </ContactFormGroup>
              <ContactFormGroup>
                <label>Last Name</label>
                <input
                  type="text"
                  value={contactCard.lastName}
                  onChange={e => setContactCard({ ...contactCard, lastName: e.target.value })}
                  placeholder="Plotka"
                />
              </ContactFormGroup>
              <ContactFormGroup>
                <label>Title</label>
                <input
                  type="text"
                  value={contactCard.title}
                  onChange={e => setContactCard({ ...contactCard, title: e.target.value })}
                  placeholder="Professor, CEO"
                />
              </ContactFormGroup>
              <ContactFormGroup>
                <label>Organization</label>
                <input
                  type="text"
                  value={contactCard.organization}
                  onChange={e => setContactCard({ ...contactCard, organization: e.target.value })}
                  placeholder="RPI, WayFong"
                />
              </ContactFormGroup>
              <ContactFormGroup>
                <label>Email</label>
                <input
                  type="email"
                  value={contactCard.email}
                  onChange={e => setContactCard({ ...contactCard, email: e.target.value })}
                  placeholder="email@example.com"
                />
              </ContactFormGroup>
              <ContactFormGroup>
                <label>Phone</label>
                <input
                  type="tel"
                  value={contactCard.phone}
                  onChange={e => setContactCard({ ...contactCard, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </ContactFormGroup>
              <ContactFormGroup>
                <label>Website</label>
                <input
                  type="url"
                  value={contactCard.website}
                  onChange={e => setContactCard({ ...contactCard, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </ContactFormGroup>
              <ContactFormGroup>
                <label>LinkedIn URL</label>
                <input
                  type="url"
                  value={contactCard.linkedin}
                  onChange={e => setContactCard({ ...contactCard, linkedin: e.target.value })}
                  placeholder="https://linkedin.com/in/..."
                />
              </ContactFormGroup>
              <ContactFormGroup>
                <label>Twitter/X URL</label>
                <input
                  type="url"
                  value={contactCard.twitter}
                  onChange={e => setContactCard({ ...contactCard, twitter: e.target.value })}
                  placeholder="https://twitter.com/..."
                />
              </ContactFormGroup>
              <ContactFormGroup>
                <label>GitHub URL</label>
                <input
                  type="url"
                  value={contactCard.github}
                  onChange={e => setContactCard({ ...contactCard, github: e.target.value })}
                  placeholder="https://github.com/..."
                />
              </ContactFormGroup>
              <ContactFormGroup $fullWidth>
                <label>Address</label>
                <input
                  type="text"
                  value={contactCard.address}
                  onChange={e => setContactCard({ ...contactCard, address: e.target.value })}
                  placeholder="123 Main St, City, State 12345"
                />
              </ContactFormGroup>
              <ContactFormGroup $fullWidth>
                <label>Note</label>
                <textarea
                  value={contactCard.note}
                  onChange={e => setContactCard({ ...contactCard, note: e.target.value })}
                  placeholder="Additional notes for the contact card"
                  rows={3}
                />
              </ContactFormGroup>
            </FormGrid>
            <SaveButtonRow>
              <ActionButton onClick={handleSaveContactCard} disabled={savingContact}>
                {savingContact ? 'Saving...' : 'Save Contact Card'}
              </ActionButton>
              <a href={`${window.location.origin}/api/linktree/vcard`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '1rem' }}>
                <ActionButton as="span" style={{ background: 'var(--bg-secondary)' }}>
                  Preview vCard
                </ActionButton>
              </a>
            </SaveButtonRow>
          </CollapsibleContent>
        )}
      </CollapsibleSection>

      <Table>
        <thead>
          <tr>
            <th>Order</th>
            <th>Title</th>
            <th>URL</th>
            <th>Icon</th>
            <th>Clicks</th>
            <th>Published</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {links.length === 0 ? (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                No links yet. Create your first link!
              </td>
            </tr>
          ) : (
            links.map(link => (
              <tr key={link._id}>
                <td>{link.order}</td>
                <td>
                  <strong>{link.title}</strong>
                  {link.subtitle && (
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                      {link.subtitle}
                    </div>
                  )}
                </td>
                <td>
                  <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>
                    {link.url.length > 40 ? `${link.url.substring(0, 40)}...` : link.url}
                  </a>
                  {link.shortUrl && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                      Short: {link.shortUrl}
                    </div>
                  )}
                </td>
                <td>{link.icon || '-'}</td>
                <td>{link.clickCount || 0}</td>
                <td>
                  {link.published ? (
                    <Badge $variant="success">Yes</Badge>
                  ) : (
                    <Badge $variant="warning">No</Badge>
                  )}
                </td>
                <td>
                  <ActionButtons>
                    <IconButton onClick={() => handleEdit(link)} title="Edit">
                      <FiEdit2 />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(link._id)} title="Delete" $danger>
                      <FiTrash2 />
                    </IconButton>
                  </ActionButtons>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <EditableModal
          isOpen={showCreateModal || showEditModal}
          isCreating={showCreateModal}
          onClose={handleCancel}
          onSave={handleSave}
          title={showCreateModal ? 'Create Link' : 'Edit Link'}
        >
          <label>Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., LinkedIn, GitHub"
            required
          />

          <label>Subtitle</label>
          <input
            type="text"
            value={formData.subtitle}
            onChange={e => setFormData({ ...formData, subtitle: e.target.value })}
            placeholder="Optional description"
          />

          <label>URL *</label>
          <input
            type="url"
            value={formData.url}
            onChange={e => setFormData({ ...formData, url: e.target.value })}
            placeholder="https://example.com"
            required
          />

          <label>Icon</label>
          <input
            type="text"
            value={formData.icon}
            onChange={e => setFormData({ ...formData, icon: e.target.value })}
            placeholder="e.g., linkedin, github, mail, /path/to/logo.png, or https://example.com/logo.png"
          />
          <small style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            Icon name (linkedin, github, twitter, instagram, mail), image path (/path/to/logo.png), or image URL (https://example.com/logo.png). Leave empty for default.
          </small>

          <label>Display Order</label>
          <input
            type="number"
            value={formData.order}
            onChange={e => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
            placeholder="0"
          />

          <label>
            <input
              type="checkbox"
              checked={formData.published}
              onChange={e => setFormData({ ...formData, published: e.target.checked })}
            />
            {' '}Published
          </label>
        </EditableModal>
      )}
    </PanelContainer>
  );
};

// ========================================
// CONFIGS PANEL
// ========================================
const ConfigsPanel = ({ refreshKey }) => {
  const [configs, setConfigs] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [showAddValue, setShowAddValue] = useState(null);
  const [showCourseEditModal, setShowCourseEditModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseFormData, setCourseFormData] = useState({});

  useEffect(() => {
    fetchConfigs();
    fetchCourses();
  }, [refreshKey]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await api.get('/config');
      // Filter out available_courses config since we show courses from ContentItems
      const allConfigs = res.data.data || res.data.configs || [];
      setConfigs(allConfigs.filter(config => config.key !== 'available_courses'));
    } catch (error) {
      toast.error('Failed to load configs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      // Fetch all courses including inactive ones
      const response = await api.get('/content-items/courses/dropdown?includeInactive=true');
      // Map courses to include status from the API response
      const coursesWithStatus = (response.data.data || []).map(course => ({
        ...course,
        status: course.status || 'Active' // Default to Active if status not set
      }));
      setCourses(coursesWithStatus);
    } catch (error) {
      console.error('Failed to load courses:', error);
      setCourses([]);
    }
  };


  const handleEditCourse = async (course) => {
    try {
      // Fetch full course data
      const response = await api.get(`/content-items/${course._id}`);
      const fullCourse = response.data.data;
      
      setEditingCourse(fullCourse);
      setCourseFormData({
        title: fullCourse.title || '',
        description: fullCourse.description || '',
        shortDescription: fullCourse.shortDescription || '',
        'data.courseId': fullCourse.data?.courseId || fullCourse.courseId || '',
        'data.code': fullCourse.data?.code || fullCourse.code || '',
        'data.courseCode': fullCourse.data?.courseCode || fullCourse.courseCode || '',
        'data.status': fullCourse.data?.status || fullCourse.status || 'Active',
        'data.url': fullCourse.data?.url || fullCourse.url || '',
        'data.semester': fullCourse.data?.semester || fullCourse.semester || '',
        'data.syllabus': fullCourse.data?.syllabus || fullCourse.syllabus || '',
        'data.topics': (fullCourse.data?.topics || fullCourse.topics || []).join(', '),
        'data.icon': fullCourse.data?.icon || fullCourse.icon || '',
        'metadata.order': fullCourse.metadata?.order || fullCourse.order || 0
      });
      setShowCourseEditModal(true);
    } catch (error) {
      toast.error('Failed to load course data');
      console.error(error);
    }
  };

  const handleSaveCourse = async () => {
    try {
      // Prepare update data
      const updateData = {
        title: courseFormData.title,
        description: courseFormData.description,
        shortDescription: courseFormData.shortDescription,
        metadata: {
          order: parseInt(courseFormData['metadata.order']) || 0
        },
        data: {
          courseId: courseFormData['data.courseId'],
          code: courseFormData['data.code'],
          courseCode: courseFormData['data.courseCode'],
          status: courseFormData['data.status'],
          url: courseFormData['data.url'],
          semester: courseFormData['data.semester'],
          syllabus: courseFormData['data.syllabus'],
          topics: courseFormData['data.topics'] 
            ? courseFormData['data.topics'].split(',').map(t => t.trim()).filter(t => t)
            : [],
          icon: courseFormData['data.icon']
        }
      };

      await api.put(`/content-items/${editingCourse._id}`, updateData);
      toast.success('Course updated successfully');
      setShowCourseEditModal(false);
      setEditingCourse(null);
      setCourseFormData({});
      fetchCourses(); // Refresh the list
    } catch (error) {
      toast.error('Failed to update course');
      console.error(error);
    }
  };

  const handleCancelCourseEdit = () => {
    setShowCourseEditModal(false);
    setEditingCourse(null);
    setCourseFormData({});
  };

  const handleAddValue = async (config) => {
    if (!editingValue.trim()) {
      toast.error('Value cannot be empty');
      return;
    }

    try {
      const updatedValues = [...(config.values || []), editingValue.trim()];
      await api.put(`/config/${config.key}`, {
        ...config,
        values: updatedValues
      });
      toast.success('Value added successfully');
      setShowAddValue(null);
      setEditingValue('');
      fetchConfigs();
    } catch (error) {
      toast.error('Failed to add value');
      console.error(error);
    }
  };

  const handleDeleteValue = async (config, valueIndex) => {
    if (!window.confirm('Are you sure you want to delete this value?')) return;

    try {
      const updatedValues = config.values.filter((_, idx) => idx !== valueIndex);
      await api.put(`/config/${config.key}`, {
        ...config,
        values: updatedValues
      });
      toast.success('Value deleted successfully');
      fetchConfigs();
    } catch (error) {
      toast.error('Failed to delete value');
      console.error(error);
    }
  };

  const handleEditConfig = (config) => {
    setEditingConfig(config);
    setShowModal(true);
  };

  if (loading) return <LoadingState>Loading configurations...</LoadingState>;

  return (
    <PanelContainer>
      <PanelHeader>
        <h2>System Configuration</h2>
        <span>{configs.length} config items</span>
      </PanelHeader>

      <ConfigGrid>
        {/* Special Courses Card - Shows courses from ContentItems */}
        <ConfigCard key="available_courses" style={{ border: '2px solid var(--accent-primary)' }}>
          <ConfigHeader>
            <h3>Courses</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <Badge $variant="info">ContentItems</Badge>
            </div>
          </ConfigHeader>
          <ConfigDescription>
            Edit course details and status. Click the edit button to modify course information.
          </ConfigDescription>
          <ConfigKey>Source: <code>/api/content-items/courses/dropdown</code></ConfigKey>
          <ConfigValues>
            {courses.length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {courses.map((course, idx) => {
                  const status = course.status || 'Active';
                  const getStatusColor = (s) => {
                    switch (s) {
                      case 'Active': return { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', badge: 'success' };
                      case 'Pending': return { bg: 'rgba(249, 115, 22, 0.1)', border: 'rgba(249, 115, 22, 0.3)', badge: 'warning' };
                      case 'Coming Soon': return { bg: 'rgba(234, 179, 8, 0.1)', border: 'rgba(234, 179, 8, 0.3)', badge: 'warning' };
                      case 'Inactive': return { bg: 'rgba(239, 68, 68, 0.1)', border: 'rgba(239, 68, 68, 0.3)', badge: 'danger' };
                      default: return { bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.3)', badge: 'success' };
                    }
                  };
                  const statusColors = getStatusColor(status);
                  
                  return (
                    <li key={idx} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      background: statusColors.bg,
                      borderRadius: '8px',
                      border: `1px solid ${statusColors.border}`
                    }}>
                      <span>
                        <strong>{course.name}</strong>
                        {course.id && <span style={{ color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>(ID: {course.id})</span>}
                        <Badge $variant={statusColors.badge} style={{ marginLeft: '0.5rem' }}>
                          {status}
                        </Badge>
                      </span>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <IconButton 
                          onClick={() => handleEditCourse(course)} 
                          title="Edit course"
                          style={{ 
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: '#3b82f6'
                          }}
                        >
                          <FiEdit2 />
                        </IconButton>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No courses found</p>
            )}
          </ConfigValues>
        </ConfigCard>

        {configs.map(config => (
          <ConfigCard key={config._id}>
            <ConfigHeader>
              <h3>{config.label}</h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <Badge $variant="info">{config.type}</Badge>
                <IconButton onClick={() => handleEditConfig(config)} title="Edit Config">
                  <FiEdit2 />
                </IconButton>
              </div>
            </ConfigHeader>
            <ConfigDescription>{config.description}</ConfigDescription>
            <ConfigKey>Key: <code>{config.key}</code></ConfigKey>
            <ConfigValues>
              {config.type === 'string_array' && (
                <>
                  <ul>
                    {config.values?.map((val, idx) => (
                      <li key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{val}</span>
                        <IconButton 
                          onClick={() => handleDeleteValue(config, idx)} 
                          title="Delete" 
                          $danger 
                          style={{ fontSize: '0.8rem', padding: '0.2rem' }}
                        >
                          <FiTrash2 />
                        </IconButton>
                      </li>
                    ))}
                  </ul>
                  {showAddValue === config._id ? (
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <input
                        type="text"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        placeholder="Enter new value..."
                        style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                      />
                      <IconButton onClick={() => handleAddValue(config)} title="Save">
                        <FiCheckCircle />
                      </IconButton>
                      <IconButton onClick={() => { setShowAddValue(null); setEditingValue(''); }} title="Cancel">
                        <FiX />
                      </IconButton>
                    </div>
                  ) : (
                    <ActionButton 
                      onClick={() => setShowAddValue(config._id)} 
                      style={{ marginTop: '0.5rem', fontSize: '0.9rem', padding: '0.4rem 0.8rem' }}
                    >
                      <FiPlus /> Add Value
                    </ActionButton>
                  )}
                </>
              )}
              {config.type === 'object_array' && (
                <ul>
                  {config.objectValues?.map((obj, idx) => (
                    <li key={idx}>
                      <strong>{obj.name || obj.label}</strong>
                      {obj.id && <span> ({obj.id})</span>}
                    </li>
                  ))}
                </ul>
              )}
            </ConfigValues>
          </ConfigCard>
        ))}
      </ConfigGrid>

      {showModal && editingConfig && (
        <ConfigModal
          config={editingConfig}
          onClose={() => {
            setShowModal(false);
            setEditingConfig(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingConfig(null);
            fetchConfigs();
          }}
        />
      )}

      {/* Course Edit Modal */}
      {showCourseEditModal && editingCourse && (
        <EditableModal
          isOpen={showCourseEditModal}
          isCreating={false}
          onClose={handleCancelCourseEdit}
          onSave={handleSaveCourse}
          title={`Edit Course: ${editingCourse.title || 'Untitled'}`}
        >
          <label>Title *</label>
          <input
            type="text"
            value={courseFormData.title || ''}
            onChange={e => setCourseFormData({ ...courseFormData, title: e.target.value })}
            placeholder="Course title"
            required
          />

          <label>Short Description</label>
          <textarea
            value={courseFormData.shortDescription || ''}
            onChange={e => setCourseFormData({ ...courseFormData, shortDescription: e.target.value })}
            placeholder="Short description"
            rows={2}
          />

          <label>Description</label>
          <RichTextEditor
            value={courseFormData.description || ''}
            onChange={(content) => setCourseFormData({ ...courseFormData, description: content })}
            placeholder="Full course description"
            height="200px"
          />

          <label>Course ID *</label>
          <input
            type="text"
            value={courseFormData['data.courseId'] || ''}
            onChange={e => setCourseFormData({ ...courseFormData, 'data.courseId': e.target.value })}
            placeholder="e.g., itws1100, rp001"
            required
          />

          <label>Course Code</label>
          <input
            type="text"
            value={courseFormData['data.code'] || ''}
            onChange={e => setCourseFormData({ ...courseFormData, 'data.code': e.target.value })}
            placeholder="e.g., ITWS 1100, RP001"
          />

          <label>Status</label>
          <select
            value={courseFormData['data.status'] || 'Active'}
            onChange={e => setCourseFormData({ ...courseFormData, 'data.status': e.target.value })}
          >
            <option value="Active">Active</option>
            <option value="Pending">Pending</option>
            <option value="Coming Soon">Coming Soon</option>
            <option value="Inactive">Inactive</option>
          </select>

          <label>URL</label>
          <input
            type="text"
            value={courseFormData['data.url'] || ''}
            onChange={e => setCourseFormData({ ...courseFormData, 'data.url': e.target.value })}
            placeholder="e.g., itws1100, rp001"
          />

          <label>Semester</label>
          <input
            type="text"
            value={courseFormData['data.semester'] || ''}
            onChange={e => setCourseFormData({ ...courseFormData, 'data.semester': e.target.value })}
            placeholder="e.g., Fall 2024"
          />

          <label>Topics (comma-separated)</label>
          <input
            type="text"
            value={courseFormData['data.topics'] || ''}
            onChange={e => setCourseFormData({ ...courseFormData, 'data.topics': e.target.value })}
            placeholder="e.g., HTML/CSS, JavaScript, Web Architecture"
          />

          <label>Icon</label>
          <input
            type="text"
            value={courseFormData['data.icon'] || ''}
            onChange={e => setCourseFormData({ ...courseFormData, 'data.icon': e.target.value })}
            placeholder="Icon name for achievements"
          />

          <label>Display Order</label>
          <input
            type="number"
            value={courseFormData['metadata.order'] || 0}
            onChange={e => setCourseFormData({ ...courseFormData, 'metadata.order': parseInt(e.target.value) || 0 })}
            placeholder="Display order"
          />

        </EditableModal>
      )}
    </PanelContainer>
  );
};

// ========================================
// STATS PANEL
// ========================================
const StatsPanel = ({ refreshKey }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [refreshKey]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/stats');
      setStats(res.data.stats);
    } catch (error) {
      toast.error('Failed to load statistics');
      console.error(error);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingState>Loading statistics...</LoadingState>;
  if (!stats) return <LoadingState>No statistics available</LoadingState>;

  // Extract values from nested structure
  const totalUsers = stats.users?.total || 0;
  const activeUsers = stats.users?.active || 0;
  const inactiveUsers = stats.users?.inactive || 0;
  const adminCount = stats.users?.byRole?.admin || 0;
  const studentCount = stats.users?.byRole?.student || 0;
  const regularUserCount = stats.users?.byRole?.user || 0;
  
  const totalContentItems = stats.content?.total || 0;
  const publishedContent = stats.content?.published || 0;
  const draftContent = stats.content?.draft || 0;
  const totalBlogs = stats.content?.byType?.blog || 0;
  const totalPortfolio = stats.content?.byType?.portfolio || 0;
  const totalJobs = stats.content?.byType?.job || 0;
  
  const totalComments = stats.comments?.total || 0;
  const approvedComments = stats.comments?.approved || 0;
  const pendingComments = stats.comments?.pending || 0;
  
  const totalMessages = stats.messages?.total || 0;
  const totalConfigs = stats.configs?.total || 0;
  
  const publishedBlogs = stats.blogs?.published || 0;
  const draftBlogs = stats.blogs?.draft || 0;

  return (
    <PanelContainer>
      <PanelHeader>
        <h2>System Statistics</h2>
      </PanelHeader>

      <StatsGrid>
        <StatCard>
          <StatIcon><FiUsers /></StatIcon>
          <StatValue>{totalUsers}</StatValue>
          <StatLabel>Total Users</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon><FiFileText /></StatIcon>
          <StatValue>{totalBlogs}</StatValue>
          <StatLabel>Blog Posts</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon><FiMessageSquare /></StatIcon>
          <StatValue>{totalComments}</StatValue>
          <StatLabel>Comments</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon><FiMail /></StatIcon>
          <StatValue>{totalMessages}</StatValue>
          <StatLabel>Contact Messages</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon><FiGrid /></StatIcon>
          <StatValue>{totalContentItems}</StatValue>
          <StatLabel>Content Items</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon><FiSettings /></StatIcon>
          <StatValue>{totalConfigs}</StatValue>
          <StatLabel>Configurations</StatLabel>
        </StatCard>
      </StatsGrid>

      <StatsDetails>
        <h3>User Breakdown</h3>
        <DetailsList>
          <li>Admins: {adminCount}</li>
          <li>Students: {studentCount}</li>
          <li>Regular Users: {regularUserCount}</li>
          <li>Active Users: {activeUsers}</li>
          <li>Inactive Users: {inactiveUsers}</li>
        </DetailsList>

        <h3>Content Breakdown</h3>
        <DetailsList>
          <li>Total Content: {totalContentItems}</li>
          <li>Published: {publishedContent}</li>
          <li>Drafts: {draftContent}</li>
          <li>Blog Posts: {totalBlogs}</li>
          <li>Portfolio Items: {totalPortfolio}</li>
          <li>Job Entries: {totalJobs}</li>
        </DetailsList>
        
        <h3>Comments & Messages</h3>
        <DetailsList>
          <li>Total Comments: {totalComments}</li>
          <li>Approved Comments: {approvedComments}</li>
          <li>Pending Comments: {pendingComments}</li>
          <li>Contact Messages: {totalMessages}</li>
        </DetailsList>
        
        <h3>Blog Statistics</h3>
        <DetailsList>
          <li>Published Blogs: {publishedBlogs}</li>
          <li>Draft Blogs: {draftBlogs}</li>
          <li>Total Blogs: {totalBlogs}</li>
        </DetailsList>
      </StatsDetails>
    </PanelContainer>
  );
};

// ========================================
// MODALS (Simplified - you'll need to expand these)
// ========================================
const ConfigModal = ({ config, onClose, onSave }) => {
  const [formData, setFormData] = useState(config);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/config/${config.key}`, formData);
      toast.success('Configuration updated');
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleValueChange = (index, newValue) => {
    const updatedValues = [...formData.values];
    updatedValues[index] = newValue;
    setFormData({ ...formData, values: updatedValues });
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <h2>Edit Configuration: {config.label}</h2>
          <CloseButton onClick={onClose}><FiX /></CloseButton>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Label</label>
            <input
              type="text"
              value={formData.label}
              onChange={e => setFormData({ ...formData, label: e.target.value })}
              required
            />
          </FormGroup>
          <FormGroup>
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </FormGroup>
          {formData.type === 'string_array' && (
            <FormGroup>
              <label>Values (one per line)</label>
              {formData.values?.map((val, idx) => (
                <input
                  key={idx}
                  type="text"
                  value={val}
                  onChange={e => handleValueChange(idx, e.target.value)}
                  style={{ marginBottom: '0.5rem' }}
                />
              ))}
            </FormGroup>
          )}
          <ModalActions>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit">Save Changes</button>
          </ModalActions>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// ========================================
// STYLED COMPONENTS
// ========================================
const DashboardContainer = styled.div`
  min-height: 100vh;
  background: var(--bg-primary);
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--accent-gradient);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const TabNav = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
`;

const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: ${props => props.$active ? 'var(--accent-gradient)' : 'var(--bg-secondary)'};
  color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.2s;

  svg {
    font-size: 1.2rem;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ContentArea = styled.div`
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 2rem;
  min-height: 600px;
`;

const PanelContainer = styled.div`
  width: 100%;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const BulkActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const BulkButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.$secondary ? '#ef4444' : '#10b981'};
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    font-size: 1.8rem;
    color: var(--text-primary);
  }

  span {
    color: var(--text-secondary);
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--accent-gradient);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-primary);
  border-radius: 8px;
  overflow: hidden;

  thead {
    background: var(--bg-tertiary);
    
    th {
      padding: 1rem;
      text-align: left;
      font-weight: 600;
      color: var(--text-primary);
      border-bottom: 2px solid var(--border-color);
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid var(--border-color);
      transition: background 0.2s;

      &:hover {
        background: var(--bg-secondary);
      }
    }

    td {
      padding: 1rem;
      color: var(--text-primary);
    }
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$variant) {
      case 'success': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'danger': return '#ef4444';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  }};
  color: white;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  padding: 0.5rem;
  background: ${props => props.$danger ? '#ef4444' : 'var(--bg-secondary)'};
  color: ${props => props.$danger ? 'white' : 'var(--text-primary)'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 1.1rem;
  }

  &:hover {
    transform: translateY(-2px);
    background: ${props => props.$danger ? '#dc2626' : 'var(--bg-tertiary)'};
  }
`;

const CommentText = styled.div`
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: var(--text-secondary);
  font-size: 1.2rem;
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }
`;

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const ConfigCard = styled.div`
  background: var(--bg-primary);
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
`;

const ConfigHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;

  h3 {
    font-size: 1.2rem;
    color: var(--text-primary);
  }
`;

const ConfigDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const ConfigKey = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 1rem;

  code {
    background: var(--bg-tertiary);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
  }
`;

const ConfigValues = styled.div`
  ul {
    list-style: none;
    padding: 0;
    
    li {
      padding: 0.5rem;
      background: var(--bg-secondary);
      margin-bottom: 0.5rem;
      border-radius: 4px;
      font-size: 0.9rem;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: var(--bg-primary);
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  border: 1px solid var(--border-color);
`;

const StatIcon = styled.div`
  font-size: 2.5rem;
  color: var(--accent-primary);
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const StatsDetails = styled.div`
  background: var(--bg-primary);
  border-radius: 8px;
  padding: 2rem;

  h3 {
    color: var(--text-primary);
    margin-bottom: 1rem;
  }
`;

const DetailsList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;

  li {
    padding: 0.75rem;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-primary);

    &:last-child {
      border-bottom: none;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h2 {
    color: var(--text-primary);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-primary);
  padding: 0.5rem;

  &:hover {
    color: var(--accent-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--text-primary);
    font-weight: 600;
  }

  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    background: var(--bg-primary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: var(--accent-primary);
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;

  button {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    transition: transform 0.2s;

    &:first-child {
      background: var(--bg-tertiary);
      color: var(--text-primary);
    }

    &:last-child {
      background: var(--accent-gradient);
      color: white;
    }

    &:hover {
      transform: translateY(-2px);
    }
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  color: var(--text-primary);
  padding: 0.5rem;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: var(--bg-secondary);
  }

  input[type="checkbox"] {
    width: auto;
    cursor: pointer;
  }

  span {
    font-weight: 400;
  }
`;

const InfoBox = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: var(--text-primary);

  code {
    background: var(--bg-tertiary);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    color: var(--accent-primary);
  }
`;

const CollapsibleSection = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const CollapsibleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  cursor: pointer;
  background: var(--bg-secondary);
  font-weight: 600;
  color: var(--text-primary);
  transition: background 0.2s ease;

  &:hover {
    background: var(--bg-tertiary);
  }
`;

const CollapsibleContent = styled.div`
  padding: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  ${props => props.$fullWidth && 'grid-column: 1 / -1;'}

  label {
    font-weight: 500;
    font-size: 0.875rem;
    color: var(--text-secondary);
  }

  input, textarea {
    padding: 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--bg-primary);
    color: var(--text-primary);
    font-size: 0.9rem;

    &:focus {
      outline: none;
      border-color: var(--accent-primary);
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }
`;

const SaveButtonRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
`;

// ========================================
// ADMIN DASHBOARD USER MANAGEMENT STYLED COMPONENTS
// (Exact copies from AdminDashboard for user management)
// ========================================

const UsersTable = styled.div`
  background: var(--bg-secondary);
  border-radius: 1rem;
  border: 1px solid var(--border-color);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1.5fr 2fr 1fr 1.5fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: var(--bg-tertiary);
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-secondary);
  text-transform: uppercase;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1.5fr 2fr 1fr 1.5fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  transition: background 0.2s;

  &:hover {
    background: var(--bg-tertiary);
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-primary);

  @media (max-width: 1024px) {
    justify-content: flex-start;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
`;

const UserStatusIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    if (props.hasIssue) return '#ef4444'; // Red for issues
    if (props.isLoggedIn) return '#10b981'; // Green for logged in
    return 'transparent'; // No indicator
  }};
  flex-shrink: 0;
  margin-right: ${props => (props.isLoggedIn || props.hasIssue) ? '0.5rem' : '0'};
  transition: all 0.2s ease;
  
  ${props => (props.isLoggedIn || props.hasIssue) && `
    box-shadow: 0 0 0 2px ${props.hasIssue ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)'};
  `}
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--accent-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.125rem;
`;

const UserName = styled.div`
  font-weight: 500;
`;

const UserEmail = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const RoleBadge = styled.span`
  padding: 0.375rem 0.875rem;
  border-radius: 2rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    if (props.role === 'admin') return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    if (props.role === 'student') return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
    return 'var(--bg-tertiary)';
  }};
  color: ${props => (props.role === 'admin' || props.role === 'student') ? 'white' : 'var(--text-secondary)'};
`;

const LandingPageBadge = styled.span`
  padding: 0.375rem 0.75rem;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;
`;

const CourseList = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const CourseBadge = styled.span`
  padding: 0.375rem 0.75rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
`;

const NoAccess = styled.span`
  font-size: 0.875rem;
  color: var(--text-tertiary);
  font-style: italic;
`;

const CancelButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-quaternary);
  }
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
`;

const StatusSelect = styled.select`
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.75rem;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }

  &:hover {
    border-color: var(--accent-primary);
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.875rem;
  border-radius: 2rem;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.active 
    ? 'rgba(16, 185, 129, 0.1)'
    : 'rgba(239, 68, 68, 0.1)'
  };
  color: ${props => props.active 
    ? 'rgb(16, 185, 129)'
    : 'rgb(239, 68, 68)'
  };
`;

const AdminActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const AdminActionButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: ${props => props.danger ? 'rgba(239, 68, 68, 0.1)' : 'var(--bg-tertiary)'};
  color: ${props => props.danger ? 'rgb(239, 68, 68)' : 'var(--text-primary)'};
  transition: all 0.2s;
  font-size: 1rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: ${props => props.danger ? 'rgba(239, 68, 68, 0.2)' : 'var(--accent-primary)'};
    color: ${props => props.danger ? 'rgb(239, 68, 68)' : 'white'};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
`;

const ModalForm = styled.form`
  padding: 2rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    background: var(--bg-primary);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    background: var(--bg-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DeleteModalBody = styled.div`
  padding: 2rem;
  text-align: center;
`;

const DeleteWarningIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  color: rgb(239, 68, 68);
  font-size: 2rem;
`;

const DeleteMessage = styled.p`
  font-size: 1.125rem;
  color: var(--text-primary);
  margin-bottom: 1rem;
  
  strong {
    color: var(--accent-primary);
  }
`;

const DeleteWarning = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const DeleteButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: rgb(239, 68, 68);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover {
    background: rgb(220, 38, 38);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RSSStatusCard = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
`;

const RSSStatusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  h3 {
    margin: 0;
    color: var(--text-primary);
    font-size: 1.25rem;
  }
`;

const RSSStatusContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatusLabel = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  min-width: 120px;
`;

const RSSStatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => props.$valid ? '#10b981' : '#ef4444'};
  color: white;
`;

const StatusValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
`;

const RSSLink = styled.a`
  color: var(--accent-primary);
  text-decoration: none;
  font-size: 0.875rem;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
`;

const IssuesList = styled.div`
  padding: 1rem;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 0.5rem;
  border-left: 3px solid #ef4444;

  strong {
    display: block;
    margin-bottom: 0.5rem;
    color: #ef4444;
  }
`;

const IssueItem = styled.div`
  margin-top: 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const WarningsList = styled.div`
  padding: 1rem;
  background: rgba(245, 158, 11, 0.1);
  border-radius: 0.5rem;
  border-left: 3px solid #f59e0b;

  strong {
    display: block;
    margin-bottom: 0.5rem;
    color: #f59e0b;
  }
`;

const WarningItem = styled.div`
  margin-top: 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const FeedLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const FeedLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--accent-gradient);
  color: white;
  text-decoration: none;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

export default SuperAdmin;
